import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

const ConstrainedDialog: React.FC<
  React.ComponentProps<typeof DialogPrimitive.Root>
> = (props) => {
  // Non-modal root so content outside remains interactive (e.g., sidebars)
  return <DialogPrimitive.Root modal={false} {...props} />;
};

const ConstrainedDialogTrigger = DialogPrimitive.Trigger;

const ConstrainedDialogClose = DialogPrimitive.Close;

// Portal constrained to the dashboard content container
// Use layout effect to ensure the container is resolved after mount,
// which avoids falling back to <body> when triggered early (e.g., from sidebars)
const ConstrainedDialogPortal = React.forwardRef<
  never,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>
>(({ children, ...props }, _ref) => {
  const [container, setContainer] = React.useState<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(
      "#dashboard-main-content"
    ) as HTMLElement | null;
    if (el && el !== container) {
      setContainer(el);
    }
  }, [container]);

  return (
    <DialogPrimitive.Portal container={container || undefined} {...props}>
      {children}
    </DialogPrimitive.Portal>
  );
});
ConstrainedDialogPortal.displayName = "ConstrainedDialogPortal";

// Note: We use a custom in-portal overlay instead of Radix's Overlay so that
// sidebars can remain interactive while the middle area is dimmed/blocked.

const ConstrainedDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onInteractOutside, ...props }, ref) => {
  // Utility: resolve if an event occurred inside a sidebar (left or chat)
  const isInSidebar = React.useCallback((originalEvent: any) => {
    const getPath = () =>
      typeof originalEvent?.composedPath === "function"
        ? (originalEvent.composedPath() as Array<EventTarget>)
        : undefined;

    const matchesSidebar = (el: any) => {
      if (!el || !(el instanceof Element)) return false;
      if (el.closest('[data-sidebar="sidebar"]')) return true;
      if (el.closest(".resizable-chat-sidebar")) return true;
      return false;
    };

    const path = getPath();
    if (path && path.length) {
      for (const el of path) {
        if (matchesSidebar(el)) return true;
      }
      return false;
    }

    let target = originalEvent?.target as Element | null | undefined;
    if (target && target.nodeType === Node.TEXT_NODE) {
      target = (target as any).parentElement as Element | null;
    }
    return !!target && matchesSidebar(target);
  }, []);

  const handlePointerDownOutside = React.useCallback(
    (event: any) => {
      const originalEvent: any = event?.detail?.originalEvent || event;
      if (isInSidebar(originalEvent)) {
        event.preventDefault();
        return;
      }
      // Allow default for middle area so onInteractOutside can close the modal
    },
    [isInSidebar]
  );

  const handleFocusOutside = React.useCallback((event: any) => {
    // Prevent all focus outside events from closing the modal
    // Focus management should not close modals - only clicks should
    event.preventDefault();
  }, []);

  // Outside clicks in sidebars: don't close. In middle: close.
  const handleInteractOutside = React.useCallback(
    (event: any) => {
      const originalEvent: any = event?.detail?.originalEvent || event;

      // Special handling for focus events - these should NEVER close the modal
      // Focus management is handled separately and shouldn't trigger modal close
      if (
        originalEvent?.type === "focus" ||
        originalEvent?.type === "focusin" ||
        originalEvent?.type === "focusout"
      ) {
        event.preventDefault();
        return;
      }

      if (isInSidebar(originalEvent)) {
        event.preventDefault();
        return;
      }
      // Middle content: allow close (no preventDefault)
    },
    [isInSidebar]
  );

  // Handle overlay clicks - close modal if clicking in middle area, ignore if clicking sidebars
  const handleOverlayClick = React.useCallback((e: React.MouseEvent) => {
    const target = e.target as Element;
    const inLeftSidebar = !!target.closest('[data-sidebar="sidebar"]');
    const inChatSidebar = !!target.closest(".resizable-chat-sidebar");

    if (!inLeftSidebar && !inChatSidebar) {
      // Click in middle area - close modal
      // Find the dialog close trigger and click it
      const closeButton = (e.currentTarget as Element).querySelector(
        "[data-radix-dialog-close]"
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        // Fallback - trigger escape key to close
        const escapeEvent = new KeyboardEvent("keydown", {
          key: "Escape",
          bubbles: true,
        });
        document.dispatchEvent(escapeEvent);
      }
    } else {
      console.log("[ConstrainedDialog] overlay click in sidebar - ignoring");
    }
  }, []);

  // No need to track dashboard bounds; the overlay is positioned with CSS

  // Prevent stacking with ownership semantics and guard against stale flags
  const ownerId = React.useMemo(() => {
    // Prefer crypto.randomUUID when available
    const rnd =
      (typeof crypto !== "undefined" && (crypto as any).randomUUID?.()) ||
      `owner-${Math.random().toString(36).slice(2)}`;
    return String(rnd);
  }, []);

  const [blockedByAnother, setBlockedByAnother] = React.useState(false);

  React.useLayoutEffect(() => {
    const body: any = document.body as any;
    const existingOwnerId: string | undefined = body.__constrainedModalOwnerId;

    // Helper to check if an element for an owner actually exists (prevents stale lockouts)
    const ownerElementExists = (id?: string) => {
      if (!id) return false;
      return !!document.querySelector(
        `[data-constrained-dialog-owner="${CSS.escape(id)}"]`
      );
    };

    if (!existingOwnerId) {
      // No current owner → take ownership
      body.__constrainedModalOpen = true;
      body.__constrainedModalOwnerId = ownerId;
      setBlockedByAnother(false);
    } else if (existingOwnerId === ownerId) {
      // We already own it
      setBlockedByAnother(false);
    } else if (!ownerElementExists(existingOwnerId)) {
      // Stale lock → reclaim ownership
      body.__constrainedModalOpen = true;
      body.__constrainedModalOwnerId = ownerId;
      setBlockedByAnother(false);
    } else {
      // Another live modal is open → block this one
      setBlockedByAnother(true);
    }

    return () => {
      // Release ownership only if we are the owner
      if (body.__constrainedModalOwnerId === ownerId) {
        delete body.__constrainedModalOpen;
        delete body.__constrainedModalOwnerId;
      }
    };
  }, [ownerId]);

  if (blockedByAnother) {
    return null;
  }

  return (
    <ConstrainedDialogPortal>
      {/* Manual overlay that blocks middle area but allows sidebar interaction */}
      <div
        className="absolute inset-0 z-[90] bg-black/60 cursor-pointer"
        onClick={handleOverlayClick}
        style={{
          pointerEvents: "auto",
        }}
      />
      <div className="absolute inset-0 z-[95] flex items-center justify-center pointer-events-none">
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "relative z-50 grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg overflow-hidden",
            className
          )}
          style={{ pointerEvents: "auto" }} // Re-enable pointer events for the modal content
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          onInteractOutside={handleInteractOutside}
          data-constrained-dialog-owner={ownerId}
          {...props}
        >
          {children}
          <DialogPrimitive.Close
            data-radix-dialog-close
            className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </div>
    </ConstrainedDialogPortal>
  );
});
ConstrainedDialogContent.displayName = "ConstrainedDialogContent";

const ConstrainedDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
ConstrainedDialogHeader.displayName = "ConstrainedDialogHeader";

const ConstrainedDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
ConstrainedDialogFooter.displayName = "ConstrainedDialogFooter";

const ConstrainedDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
ConstrainedDialogTitle.displayName = "ConstrainedDialogTitle";

const ConstrainedDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
ConstrainedDialogDescription.displayName = "ConstrainedDialogDescription";

export {
  ConstrainedDialog,
  ConstrainedDialogPortal,
  ConstrainedDialogClose,
  ConstrainedDialogTrigger,
  ConstrainedDialogContent,
  ConstrainedDialogHeader,
  ConstrainedDialogFooter,
  ConstrainedDialogTitle,
  ConstrainedDialogDescription,
};
